// Refactored from https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial

import {
    AuthenticationResult,
    InteractionRequiredAuthError,
    IPublicClientApplication,
    PublicClientApplication
} from "@azure/msal-browser";

interface AuthSetup {
    msalConfig: {
        auth: {
            clientId: string;
            authority: string;
            knownAuthorities?: string[];
            redirectUri: string;
            postLogoutRedirectUri: string;
            navigateToLoginRequestUrl: boolean;
        };
        cache: {
            cacheLocation: string;
            storeAuthStateInCookie: boolean;
        };
    };
    loginRequest: {
        scopes: Array<string>;
    };
    tokenRequest: {
        scopes: Array<string>;
    };
}

interface DualAuthSetup {
    azuread: AuthSetup;
    b2c: AuthSetup;
    b2cEnabled?: boolean;
}

export type AuthType = "azuread" | "b2c";

const AUTH_TYPE_KEY = "auth_type";

async function fetchAuthSetup(): Promise<DualAuthSetup> {
    const response = await fetch('/auth_setup');
    if (!response.ok) {
        throw new Error(`Auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

const authSetup = await fetchAuthSetup();

export const msalInstanceAzureAD: IPublicClientApplication = new PublicClientApplication(authSetup.azuread.msalConfig);
export const msalInstanceB2C: IPublicClientApplication = new PublicClientApplication(authSetup.b2c.msalConfig);

await msalInstanceAzureAD.initialize();
await msalInstanceB2C.initialize();

export const azureADConfig = authSetup.azuread;
export const b2cConfig = authSetup.b2c;

// B2C is built but feature-flagged off (backend B2C_ENABLED) until the client
// is ready. While disabled, a stored "b2c" auth type is never honoured, so all
// token acquisition and login flows resolve to Azure AD.
export const b2cEnabled = authSetup.b2cEnabled === true;

export function getActiveAuthType(): AuthType | null {
    const authType = localStorage.getItem(AUTH_TYPE_KEY) as AuthType | null;
    if (authType === "b2c" && !b2cEnabled) {
        return null;
    }
    return authType;
}

export function setActiveAuthType(authType: AuthType): void {
    localStorage.setItem(AUTH_TYPE_KEY, authType);
}

export function clearActiveAuthType(): void {
    localStorage.removeItem(AUTH_TYPE_KEY);
}

export function getActiveMsalInstance(): IPublicClientApplication {
    const authType = getActiveAuthType();
    return authType === "b2c" ? msalInstanceB2C : msalInstanceAzureAD;
}

export type AcquireTokenResult =
    | { kind: "success"; result: AuthenticationResult }
    | { kind: "interaction-required"; error: Error }
    | { kind: "failure"; error: unknown };

export const acquireToken = async (options?: { forceRefresh?: boolean }): Promise<AcquireTokenResult> => {
    const instance = getActiveMsalInstance();
    const config = getActiveAuthType() === "b2c" ? b2cConfig : azureADConfig;
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

    if (!account) {
        return { kind: "interaction-required", error: new Error("No signed-in account") };
    }

    try {
        const result = await instance.acquireTokenSilent({
            ...config.tokenRequest,
            redirectUri: config.msalConfig.auth.redirectUri,
            account,
            forceRefresh: options?.forceRefresh ?? false
        });
        return { kind: "success", result };
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            return { kind: "interaction-required", error };
        }
        console.error("Token acquisition failed:", error);
        return { kind: "failure", error };
    }
};